import {
	Background,
	Controls,
	Edge,
	EdgeChange,
	Node,
	NodeChange,
	ReactFlow,
	applyEdgeChanges,
	applyNodeChanges,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from '@xyflow/react';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { useReactFlowAutoFit } from '@@common';

import { EnumModel, SchemaHierarchyModel } from '../../api/api';
import { usePortalTheme } from '../../themes/PortalThemeProvider';
import { flowThemeTokens } from '../../themes/flowTokens';
import { domainGraphNodeTypes } from './Components/DomainGraphNodes';
import { getSelectedNodeId } from './DomainGraphIdentity';
import { applyDomainGraphLayout, getGraphStateKey } from './DomainGraphLayout';
import {
	DomainGraphNodeFilter,
	DomainMetadataUpdateItem,
	DomainNodeData,
	buildDomainGraph,
	filterDomainGraph,
	getNodeDimensions,
	toDomainMetadataUpdateItem,
	withPositionMetadata,
} from './DomainGraphUtils';
import { useDomainMetadataWriteQueue } from './Hooks/useDomainMetadataWriteQueue';

type DomainEntityFlowProps = {
	entities: SchemaHierarchyModel[];
	enums: EnumModel[];
	selectedEntityId?: string;
	selectedEnumId?: string;
	enabledNodeFilters: DomainGraphNodeFilter[];
	searchText: string;
	autoLayoutRequest: number;
	onEntitySelected?: (entityId: string) => void;
	onEnumSelected?: (enumId: string) => void;
	onBatchDomainMetadataChanged: (items: DomainMetadataUpdateItem[]) => Promise<void>;
};

export default function DomainEntityFlow({
	entities,
	enums,
	selectedEntityId,
	selectedEnumId,
	enabledNodeFilters,
	searchText,
	autoLayoutRequest,
	onEntitySelected,
	onEnumSelected,
	onBatchDomainMetadataChanged,
}: DomainEntityFlowProps) {
	useReactFlowAutoFit();
	const theme = useTheme();
	const { fitView, setCenter } = useReactFlow<Node<DomainNodeData>, Edge>();
	const { themeName } = usePortalTheme();
	const flowTokens = flowThemeTokens[themeName];
	const [isApplyingLayout, setIsApplyingLayout] = useState(false);
	const previousSelectedNodeIdRef = useRef<string>();
	const handledAutoLayoutRequestRef = useRef(0);
	const syncedGraphStateKeyRef = useRef('');

	const { flowNodes, flowEdges } = useMemo(
		() => buildDomainGraph(entities, enums, selectedEntityId, selectedEnumId),
		[entities, enums, selectedEntityId, selectedEnumId],
	);

	const { visibleFlowNodes, visibleFlowEdges } = useMemo(
		() => filterDomainGraph(flowNodes, flowEdges, enabledNodeFilters, searchText),
		[enabledNodeFilters, flowEdges, flowNodes, searchText],
	);
	const visibleGraphStateKey = useMemo(() => getGraphStateKey(visibleFlowNodes, visibleFlowEdges), [visibleFlowEdges, visibleFlowNodes]);

	const [nodes, setNodes] = useNodesState<Node<DomainNodeData>>([]);
	const [edges, setEdges] = useEdgesState<Edge>([]);
	const { queueItems, flushItems } = useDomainMetadataWriteQueue(onBatchDomainMetadataChanged);

	const onNodesChange = useCallback(
		(changes: NodeChange<Node<DomainNodeData>>[]) => {
			setNodes((currentNodes) => applyNodeChanges<Node<DomainNodeData>>(changes, currentNodes));
		},
		[setNodes],
	);

	const onEdgesChange = useCallback(
		(changes: EdgeChange<Edge>[]) => {
			setEdges((currentEdges) => applyEdgeChanges<Edge>(changes, currentEdges));
		},
		[setEdges],
	);

	useEffect(() => {
		if (syncedGraphStateKeyRef.current === visibleGraphStateKey) {
			return;
		}

		syncedGraphStateKeyRef.current = visibleGraphStateKey;
		setNodes(visibleFlowNodes);
		setEdges(visibleFlowEdges);
	}, [setEdges, setNodes, visibleFlowEdges, visibleFlowNodes, visibleGraphStateKey]);

	useEffect(() => {
		const selectedNodeId = getSelectedNodeId(selectedEntityId, selectedEnumId);
		if (previousSelectedNodeIdRef.current === selectedNodeId) {
			return;
		}

		previousSelectedNodeIdRef.current = selectedNodeId;
		if (!selectedNodeId) {
			return;
		}

		const selectedNode = visibleFlowNodes.find((node) => node.id === selectedNodeId);
		if (!selectedNode) {
			return;
		}

		const { width, height } = getNodeDimensions(selectedNode);
		window.requestAnimationFrame(() => {
			setCenter(selectedNode.position.x + width / 2, selectedNode.position.y + height / 2, { zoom: 1, duration: 450 });
		});
	}, [selectedEntityId, selectedEnumId, setCenter, visibleFlowNodes]);

	const handleNodeDragStop = useCallback(
		(_event: React.MouseEvent, node: Node<DomainNodeData>) => {
			const nodeWithMetadata = withPositionMetadata(node);
			setNodes((currentNodes) =>
				currentNodes.map((currentNode) => (currentNode.id === node.id ? { ...nodeWithMetadata, selected: currentNode.selected } : currentNode)),
			);
			queueItems([toDomainMetadataUpdateItem(nodeWithMetadata)]);
		},
		[queueItems, setNodes],
	);

	const handleAutoLayout = useCallback(async () => {
		if (isApplyingLayout) {
			return;
		}

		setIsApplyingLayout(true);

		try {
			const layoutResult = applyDomainGraphLayout({
				currentNodes: nodes,
				visibleNodes: visibleFlowNodes,
				visibleEdges: visibleFlowEdges,
			});
			if (layoutResult.nodes.length === 0) {
				return;
			}

			setNodes(layoutResult.nodes);
			setEdges(layoutResult.edges);
			window.requestAnimationFrame(() => {
				fitView({ padding: 0.2, duration: 800 });
			});

			await flushItems(layoutResult.metadataItems);
		} finally {
			setIsApplyingLayout(false);
		}
	}, [fitView, flushItems, isApplyingLayout, nodes, setEdges, setNodes, visibleFlowEdges, visibleFlowNodes]);

	useEffect(() => {
		if (autoLayoutRequest > 0 && handledAutoLayoutRequestRef.current !== autoLayoutRequest) {
			handledAutoLayoutRequestRef.current = autoLayoutRequest;
			void handleAutoLayout();
		}
	}, [autoLayoutRequest, handleAutoLayout]);

	if (flowNodes.length > 0 && visibleFlowNodes.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="body2" color="text.secondary">
					No graph nodes match the current filters.
				</Typography>
			</Box>
		);
	}

	return (
		<ReactFlow
			nodes={nodes}
			edges={edges}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			onNodeClick={(_event, node) => {
				if (node.data.kind === 'entity') {
					onEntitySelected?.(node.id);
				} else {
					onEnumSelected?.(node.data.enumModel.id);
				}
			}}
			onNodeDragStop={handleNodeDragStop}
			nodeTypes={domainGraphNodeTypes}
			fitView
			fitViewOptions={{ padding: 0.2, duration: 800 }}
			minZoom={0.35}
			maxZoom={2}
			style={{ width: '100%', height: '100%', backgroundColor: theme.palette.background.canvas ?? flowTokens.canvas }}
		>
			<Controls />
			<Background color={flowTokens.grid} gap={18} />
		</ReactFlow>
	);
}
